/**
 * Safe subset of markdown → HTML for chat bubbles (lists, bold, links, paragraphs).
 * Mirrors chatbot-widget/src/lib/messageFormat.js for consistent rendering.
 */

export function escapeHtml(text) {
  if (text == null) return "";
  const s = String(text);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function decodeBasicEntities(s) {
  return String(s)
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");
}

/**
 * Allow only http(s)/mailto links. Returns HTML-attribute-escaped href or null.
 * @param {string} url
 * @returns {string | null}
 */
export function sanitizeChatLinkHref(url) {
  const raw = decodeBasicEntities(url).trim();
  if (!raw) return null;
  let candidate = raw;
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(candidate)) {
    candidate = `https://${candidate}`;
  }
  let parsed;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }
  if (!["http:", "https:", "mailto:"].includes(parsed.protocol)) return null;
  return escapeHtml(parsed.href);
}

/**
 * Normalize a user-entered URL for markdown storage (adds https:// when missing).
 * @param {string} url
 * @returns {string | null}
 */
export function normalizeComposerLinkUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return null;
  let candidate = raw;
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(candidate)) {
    candidate = `https://${candidate}`;
  }
  try {
    const parsed = new URL(candidate);
    if (!["http:", "https:", "mailto:"].includes(parsed.protocol)) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

function formatInlineMarkdown(escaped) {
  return escaped
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label, url) => {
      const href = sanitizeChatLinkHref(url);
      if (!href) return match;
      return `<a class="chat-md-link" href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    })
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");
}

function formatListItemLine(line, pattern) {
  const text = line.trim().replace(pattern, "").trim();
  return `<li>${formatInlineMarkdown(escapeHtml(text))}</li>`;
}

function formatLinesSegment(lines) {
  const html = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    if (/^\d+\.\s+/.test(line.trim())) {
      const items = [];
      while (i < lines.length && lines[i].trim() && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(formatListItemLine(lines[i], /^\d+\.\s+/));
        i += 1;
      }
      html.push(`<ol class="chat-md-list">${items.join("")}</ol>`);
      continue;
    }
    if (/^[-*]\s+/.test(line.trim())) {
      const items = [];
      while (i < lines.length && lines[i].trim() && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(formatListItemLine(lines[i], /^[-*]\s+/));
        i += 1;
      }
      html.push(`<ul class="chat-md-list">${items.join("")}</ul>`);
      continue;
    }
    html.push(`<p class="chat-md-p">${formatInlineMarkdown(escapeHtml(line.trim()))}</p>`);
    i += 1;
  }
  return html.join("");
}

function formatBlock(block) {
  const trimmed = (block || "").trim();
  if (!trimmed) return "";
  return formatLinesSegment(trimmed.split("\n"));
}

/**
 * @param {string | null | undefined} content
 * @returns {string}
 */
export function formatChatMessageHtml(content) {
  if (content == null || content === "") return "";
  const normalized = String(content).replace(/\r\n/g, "\n").trim();
  const blocks = normalized.split(/\n\n+/);
  const parts = blocks.map((b) => formatBlock(b)).filter(Boolean);
  if (parts.length > 0) return `<div class="chat-md">${parts.join("")}</div>`;
  return formatBlock(normalized);
}
